import { Card } from '../components/ui';
import { useT, type Clave } from '../i18n';

/**
 * El manual completo, en una pantalla aparte.
 *
 * ── Por qué no está en «Bienvenida» ───────────────────────────────────────────
 * Porque son dos cosas distintas y mezclarlas arruina las dos. La bienvenida es lo que hay que
 * hacer UNA VEZ para que el panel funcione, y su valor está en ser corta: cinco tarjetas que se
 * leen de pie. Esto otro es la referencia — qué hace cada pantalla, qué significa cada opción,
 * qué pasa cuando algo falla — y se consulta salteado, meses después, buscando una cosa puntual.
 *
 * Por eso se abre en una pestaña nueva: se lee con el panel al lado, no en lugar del panel.
 *
 * ── Por qué está en el código y no en un README ───────────────────────────────
 * Porque quien lo necesita no tiene el repositorio: tiene el panel abierto y una duda. Un README
 * sirve para quien despliega; esto es para quien opera.
 *
 * ── Qué hay que hacer al cambiar la aplicación ────────────────────────────────
 * Actualizarlo. Un manual desactualizado es peor que no tenerlo: manda a buscar botones que ya
 * no existen y hace desconfiar del resto. Si agregás una opción a Consumo o una pestaña a la
 * ficha, agregala acá en el mismo commit.
 */

/** Un ancla de la tabla de contenidos. El `id` va en el `<section>` correspondiente. */
type Ancla = { id: string; titulo: Clave };

const SECCIONES: Ancla[] = [
  { id: 'como-funciona', titulo: 'man.secComoFunciona' },
  { id: 'credenciales', titulo: 'man.secCredenciales' },
  { id: 'ajustes', titulo: 'man.secAjustes' },
  { id: 'crear', titulo: 'man.secCrear' },
  { id: 'estilo', titulo: 'man.secEstilo' },
  { id: 'ficha', titulo: 'man.secFicha' },
  { id: 'redes', titulo: 'man.secRedes' },
  { id: 'contenido', titulo: 'man.secContenido' },
  { id: 'pantallas', titulo: 'man.secPantallas' },
  { id: 'cuentas', titulo: 'man.secCuentas' },
  { id: 'problemas', titulo: 'man.secProblemas' },
];

function Externo({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      className="underline underline-offset-2 hover:text-[var(--color-accent)]"
    >
      {children} ↗
    </a>
  );
}

function Bloque({
  id,
  titulo,
  children,
}: {
  id: string;
  titulo: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="scroll-mt-6 space-y-4">
      <h2 className="border-b border-[var(--color-border)] pb-2 text-xl font-semibold tracking-tight">
        {titulo}
      </h2>
      {children}
    </section>
  );
}

/** Un paso numerado. La numeración es del contenido, no de una lista automática. */
function Paso({ n, children }: { n: string; children: React.ReactNode }) {
  return (
    <li className="flex gap-3">
      <span className="shrink-0 font-mono text-xs text-[var(--color-ink-muted)]">{n}</span>
      <span>{children}</span>
    </li>
  );
}

/** Una lista de «cosa → qué es». Es la forma que más se repite acá. */
function Definiciones({ filas }: { filas: [string, React.ReactNode][] }) {
  return (
    <dl className="max-w-2xl space-y-3 text-sm">
      {filas.map(([nombre, que]) => (
        <div key={nombre} className="flex flex-col gap-0.5 sm:flex-row sm:gap-3">
          <dt className="shrink-0 font-medium sm:w-44">{nombre}</dt>
          <dd className="text-[var(--color-ink-muted)]">{que}</dd>
        </div>
      ))}
    </dl>
  );
}

export function Manual() {
  const { t } = useT();
  return (
    <div className="mx-auto max-w-3xl space-y-8 pb-16">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight">{t('man.titulo')}</h1>
        <p className="mt-1 text-sm text-[var(--color-ink-muted)]">{t('man.bajada')}</p>
      </div>

      <nav className="rounded-lg border border-[var(--color-border)] p-4">
        <ol className="grid gap-x-6 gap-y-1.5 text-sm sm:grid-cols-2">
          {SECCIONES.map((seccion, i) => (
            <li key={seccion.id} className="flex gap-2">
              <span className="font-mono text-xs text-[var(--color-ink-muted)]">
                {String(i + 1).padStart(2, '0')}
              </span>
              <a
                href={`#${seccion.id}`}
                className="underline underline-offset-2 hover:text-[var(--color-accent)]"
              >
                {t(seccion.titulo)}
              </a>
            </li>
          ))}
        </ol>
      </nav>

      {/* ── 01 ─────────────────────────────────────────────────────────────── */}
      <Bloque id="como-funciona" titulo={t('man.secComoFunciona')}>
        <Card title={t('man.problemaTitulo')}>
          <p className="max-w-2xl text-sm">
            {t('man.problemaP1Antes')}
            <strong>{t('man.mismaPersona')}</strong>
            {t('man.problemaP1Despues')}
          </p>
          <p className="mt-3 max-w-2xl text-sm text-[var(--color-ink-muted)]">
            {t('man.problemaP2Antes')}
            <strong>{t('man.adjuntando')}</strong>
            {t('man.problemaP2Despues')}
          </p>
        </Card>

        <Card title={t('man.tresEtapas')}>
          <ol className="max-w-2xl space-y-3 text-sm">
            <Paso n="01">
              <strong>{t('man.etapa1')}</strong>
              {t('man.etapa1Que')}
            </Paso>
            <Paso n="02">
              <strong>{t('man.etapa2')}</strong>
              {t('man.etapa2Antes')}
              <em>{t('man.aPartirDeLaCandidata')}</em>
              {t('man.etapa2Despues')}
            </Paso>
            <Paso n="03">
              <strong>{t('man.etapa3')}</strong>
              {t('man.etapa3Que')}
            </Paso>
          </ol>
          <p className="mt-4 max-w-2xl text-xs text-[var(--color-ink-muted)]">
            {t('man.pausasAProposito')}
          </p>
        </Card>
      </Bloque>

      {/* ── 02 ─────────────────────────────────────────────────────────────── */}
      <Bloque id="credenciales" titulo={t('man.secCredenciales')}>
        <Card title={t('man.cadaClaveEsTuya')}>
          <p className="max-w-2xl text-sm text-[var(--color-ink-muted)]">
            {t('man.cadaClaveAntes')}
            <strong>{t('man.soloLoVesVos')}</strong>
            {t('man.cadaClaveDespues')}
          </p>
        </Card>

        <Card title={t('man.lasQuePodesCargar')}>
          <Definiciones
            filas={[
              [
                t('man.cuentaCloudflare'),
                <>
                  <strong>{t('man.imprescindible')}</strong>
                  {t('man.cfAntes')}
                  <Externo href="https://dash.cloudflare.com/profile/api-tokens">
                    {t('man.cfEnlace')}
                  </Externo>
                  {t('man.cfDespues')}
                </>,
              ],
              [
                'Gemini',
                <>
                  {t('man.geminiAntes')}
                  <Externo href="https://aistudio.google.com/apikey">
                    aistudio.google.com/apikey
                  </Externo>
                  {t('man.geminiDespues')}
                </>,
              ],
              ['Groq', t('man.groqQue')],
              ['fal', t('man.falQue')],
              [t('man.casillaDeCorreo'), t('man.casillaQue')],
              [t('man.dominioDeCorreo'), t('man.dominioQue')],
              [t('man.appDeMeta'), t('man.appDeMetaQue')],
            ]}
          />
        </Card>
      </Bloque>

      {/* ── 03 ─────────────────────────────────────────────────────────────── */}
      <Bloque id="ajustes" titulo={t('man.secAjustes')}>
        <Card title={t('man.frenosDeGasto')}>
          <Definiciones
            filas={[
              [
                t('freno.modoCero'),
                <>
                  <strong>{t('man.vieneActivo')}</strong>
                  {t('man.modoCeroQue')}
                </>,
              ],
              [t('man.topeDiario'), t('man.topeDiarioQue')],
              [t('man.topeMensual'), t('man.topeMensualQue')],
              [t('man.diferidas'), t('man.diferidasQue')],
            ]}
          />
        </Card>

        <Card title={t('man.calidadDeImagen')}>
          <Definiciones
            filas={[
              [t('man.economia'), t('man.economiaQue')],
              [t('man.equilibrado'), t('man.equilibradoQue')],
              [t('man.premium'), t('man.premiumQue')],
              [t('man.sinRestricciones'), t('man.sinRestriccionesQue')],
            ]}
          />
          <p className="mt-4 max-w-2xl text-sm text-[var(--color-ink-muted)]">
            <strong>{t('man.avanzado')}</strong>
            {t('man.avanzadoAntes')}
            <strong>{t('man.soloAdmitenReferencias')}</strong>
            {t('man.avanzadoDespues')}
          </p>
        </Card>

        <Card title={t('man.videoYResto')}>
          <Definiciones
            filas={[
              [t('man.proveedorDeVideo'), t('man.proveedorDeVideoQue')],
              [t('man.almacenamiento'), t('man.almacenamientoQue')],
              [t('man.avisoEnPies'), t('man.avisoEnPiesQue')],
              [t('man.consumoPorProveedor'), t('man.consumoPorProveedorQue')],
              [t('ext.titulo'), t('man.extensionQue')],
              [t('freno.featureFlags'), t('man.featureFlagsQue')],
            ]}
          />
        </Card>
      </Bloque>

      {/* ── 04 ─────────────────────────────────────────────────────────────── */}
      <Bloque id="crear" titulo={t('man.secCrear')}>
        <Card title={t('man.loQueSeCompleta')}>
          <Definiciones
            filas={[
              [t('man.nombreYPais'), t('man.nombreYPaisQue')],
              [t('man.edad'), t('man.edadQue')],
              [t('man.deQueHabla'), t('man.deQueHablaQue')],
              [t('man.rasgos'), t('man.rasgosQue')],
              [t('man.idiomas'), t('man.idiomasQue')],
              [t('man.fichaDelPersonaje'), t('man.fichaDelPersonajeQue')],
              [t('man.alAzar'), t('man.alAzarQue')],
            ]}
          />
        </Card>
      </Bloque>

      {/* ── 05 ─────────────────────────────────────────────────────────────── */}
      <Bloque id="estilo" titulo={t('man.secEstilo')}>
        <Card title={t('man.queDefineCada')}>
          <Definiciones
            filas={[
              [t('ident.registro'), t('man.registroQue')],
              [t('ident.nivelDePiel'), t('man.nivelDePielQue')],
              [t('man.escenarioDeVestuario'), t('man.escenarioDeVestuarioQue')],
              [t('man.tipoDeCuerpo'), t('man.tipoDeCuerpoQue')],
              [t('ident.tonoDelFeed'), t('man.tonoDelFeedQue')],
              [t('man.escenarios'), t('man.escenariosQue')],
            ]}
          />
          <p className="mt-4 max-w-2xl text-sm text-[var(--color-ink-muted)]">
            <strong>{t('man.sobreAtrevido')}</strong>
            {t('man.sobreAtrevidoQue')}
          </p>
          <p className="mt-3 max-w-2xl text-xs text-[var(--color-ink-muted)]">
            {t('man.nivelesNecesitanFal')}
          </p>
        </Card>
      </Bloque>

      {/* ── 06 ─────────────────────────────────────────────────────────────── */}
      <Bloque id="ficha" titulo={t('man.secFicha')}>
        <Card title={t('man.lasNuevePestanias')}>
          <Definiciones
            filas={[
              [t('tab.resumen'), t('man.pesResumenQue')],
              [t('tab.identidad'), t('man.pesIdentidadQue')],
              [t('tab.galeria'), t('man.pesGaleriaQue')],
              [t('tab.contenido'), t('man.pesContenidoQue')],
              [t('tab.plataformas'), t('man.pesPlataformasQue')],
              [t('tab.tareas'), t('man.pesTareasQue')],
              [t('tab.workflow'), t('man.pesWorkflowQue')],
              [t('tab.consumo'), t('man.pesConsumoQue')],
              [t('tab.auditoria'), t('man.pesAuditoriaQue')],
            ]}
          />
        </Card>
      </Bloque>

      {/* ── 07 ─────────────────────────────────────────────────────────────── */}
      <Bloque id="redes" titulo={t('man.secRedes')}>
        <Card title={t('man.primeroSeCrea')}>
          <p className="max-w-2xl text-sm text-[var(--color-ink-muted)]">
            {t('man.primeroSeCreaAntes')}
            <strong>{t('man.tareas')}</strong>
            {t('man.primeroSeCreaMedio')}
            <strong>{t('man.conectar')}</strong>
            {t('man.primeroSeCreaFin')}
          </p>
          <p className="mt-3 max-w-2xl text-sm text-[var(--color-ink-muted)]">
            {t('man.tokenNoVence')}
          </p>
        </Card>

        <Card title={t('man.requisitosDeCada')}>
          <Definiciones
            filas={[
              ['Instagram', t('man.igQue')],
              ['Facebook', t('man.fbQue')],
              ['Fanvue', t('man.fanvueQue')],
            ]}
          />
        </Card>
      </Bloque>

      {/* ── 08 ─────────────────────────────────────────────────────────────── */}
      <Bloque id="contenido" titulo={t('man.secContenido')}>
        <Card title={t('man.estadosDeUna')}>
          <ol className="max-w-2xl space-y-3 text-sm">
            <Paso n="01">
              <strong>{t('man.borrador')}</strong>
              {t('man.borradorQue')}
            </Paso>
            <Paso n="02">
              <strong>{t('man.aprobada')}</strong>
              {t('man.aprobadaQue')}
            </Paso>
            <Paso n="03">
              <strong>{t('man.programada')}</strong>
              {t('man.programadaQue')}
            </Paso>
            <Paso n="04">
              <strong>{t('man.publicada')}</strong>
              {t('man.publicadaQue')}
            </Paso>
          </ol>
        </Card>

        <Card title={t('man.publicacionAutomatica')}>
          <p className="max-w-2xl text-sm text-[var(--color-ink-muted)]">
            {t('man.automaticaAntes')}
            <strong>{t('man.igYFb')}</strong>
            {t('man.automaticaDespues')}
          </p>
          <p className="mt-3 max-w-2xl text-xs text-[var(--color-ink-muted)]">
            {t('man.soloAprobadas')}
          </p>
        </Card>
      </Bloque>

      {/* ── 09 ─────────────────────────────────────────────────────────────── */}
      <Bloque id="pantallas" titulo={t('man.secPantallas')}>
        <Card title={t('man.elMenu')}>
          <Definiciones
            filas={[
              [t('panel.titulo'), t('man.menuInicioQue')],
              [t('man.menuNueva'), t('man.menuNuevaQue')],
              [t('plat.titulo'), t('man.menuPlataformasQue')],
              [t('ref.titulo'), t('man.menuLeadsQue')],
              [t('cred.consumo'), t('man.menuConsumoQue')],
              [t('man.menuServicios'), t('man.menuServiciosQue')],
              [t('cred.titulo'), t('man.menuCredencialesQue')],
              [t('man.menuGuia'), t('man.menuGuiaQue')],
              [t('man.menuLaboratorio'), t('man.menuLaboratorioQue')],
            ]}
          />
          <p className="mt-4 max-w-2xl text-sm text-[var(--color-ink-muted)]">
            {t('man.ademasEstadisticasAntes')}
            <strong>{t('man.estadisticas')}</strong>
            {t('man.ademasEstadisticasDespues')}
          </p>
        </Card>
      </Bloque>

      {/* ── 10 ────────────────────────────────────────────────────────────── */}
      <Bloque id="cuentas" titulo={t('man.secCuentas')}>
        <Card title={t('man.quienPuedeQue')}>
          <Definiciones
            filas={[
              [t('rol.admin'), t('man.rolAdminQue')],
              [t('rol.operator'), t('man.rolOperadorQue')],
              [t('man.soloLectura'), t('man.rolLecturaQue')],
            ]}
          />
          <p className="mt-4 max-w-2xl text-sm text-[var(--color-ink-muted)]">
            <strong>{t('man.cadaCuentaVacia')}</strong>
            {t('man.cadaCuentaVaciaQue')}
          </p>
        </Card>
      </Bloque>

      {/* ── 11 ────────────────────────────────────────────────────────────── */}
      <Bloque id="problemas" titulo={t('man.secProblemas')}>
        <Card title={t('man.loQueMasAparece')}>
          <dl className="max-w-2xl space-y-4 text-sm">
            {(
              [
                [t('man.pCuota'), t('man.pCuotaQue')],
                [t('man.pPermiso'), t('man.pPermisoQue')],
                [t('man.pMuro'), t('man.pMuroQue')],
                [t('man.pDistintas'), t('man.pDistintasQue')],
                [t('man.pSinBoton'), t('man.pSinBotonQue')],
                [t('man.pSinContenido'), t('man.pSinContenidoQue')],
                [t('man.pSensual'), t('man.pSensualQue')],
                [t('man.pModeloFalta'), t('man.pModeloFaltaQue')],
              ] as [string, string][]
            ).map(([sintoma, causa]) => (
              <div key={sintoma}>
                <dt className="font-medium">{sintoma}</dt>
                <dd className="mt-0.5 text-[var(--color-ink-muted)]">{causa}</dd>
              </div>
            ))}
          </dl>
        </Card>

        <Card title={t('man.antesDeGastar')}>
          <ul className="max-w-2xl list-disc space-y-2 pl-5 text-sm text-[var(--color-ink-muted)]">
            <li>
              <strong>{t('man.videoCuesta')}</strong>
              {t('man.videoCuestaQue')}
            </li>
            <li>{t('man.regenerarCuesta')}</li>
          </ul>
        </Card>
      </Bloque>
    </div>
  );
}
